import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import Dropdown from "../../components/Dropdown/Dropdown";
import { RefreshCcw } from "lucide-react";
import CountUp from "react-countup";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import UserDashboardService from "../../services/UserDashboard.service";
import ApiService from "../../services/Api.service";
import Loader from "../../components/Loader/Loader";

export default function Dashboard() {
  const [companies, setCompanies] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [durationOptions, setDurationOptions] = useState([]);

  const [activeCompany, setActiveCompany] = useState("");
  const [activeDuration, setActiveDuration] = useState("");

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ----------------------------------
     FETCH CAMPAIGNS
  ---------------------------------- */
  const fetchCampaigns = async () => {
    try {
      const res = await ApiService.getCampaignOptions();
      const data = res?.data?.data || [];
      setCampaignOptions(data);
      const durationRes = await ApiService.getDurationOptions();
      const durationData = durationRes?.data?.duration_type || [];
      setDurationOptions(durationData);
      if (data.length) {
        setActiveCompany(data[0].value);
      }
      if (durationData.length) {
        setActiveDuration(durationData[0].value);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  };

  /* ----------------------------------
     FETCH DASHBOARD DATA
  ---------------------------------- */
  const fetchDashboardData = async (campaignId) => {
    try {
       setLoading(true);
      const res = await UserDashboardService.getDashboardSummary({
        campaign_id: campaignId,
        duration:activeDuration
      });
      setCompanies([])
      setCompanies(res?.data || []);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
    setLoading(false);    }
  };

  /* ----------------------------------
     EFFECTS
  ---------------------------------- */
  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (activeCompany) {
      fetchDashboardData(activeCompany);
    }
  }, [activeCompany,activeDuration]);

  /* ----------------------------------
     HELPERS
  ---------------------------------- */
  const currentCompany = companies.find(
    (c) => c.id === activeCompany
  );

  const nextCampaign = () => {
    if (!campaignOptions.length) return;
    const idx = campaignOptions.findIndex(
      (c) => c.value === activeCompany
    );
    const next =
      campaignOptions[(idx + 1) % campaignOptions.length];
    setActiveCompany(next.value);
  };

  const prevCampaign = () => {
    if (!campaignOptions.length) return;
    const idx = campaignOptions.findIndex(
      (c) => c.value === activeCompany
    );
    const prev =
      campaignOptions[
        (idx - 1 + campaignOptions.length) %
          campaignOptions.length
      ];
    setActiveCompany(prev.value);
  };

  const handleCampaignChange = (e) => {
    setActiveCompany(e.target.value);
  };

    const handleDurationChange = (e) => {
    setActiveDuration(e.target.value);
  };

  /* ----------------------------------
     COLORS
  ---------------------------------- */
  const dispositionColors = {
    Positive: "#4CAF50",
    Negative: "#F44336",
    Neutral: "#FF9800",
    "Not Connected": "#9E9E9E",
  };

  return (
    <div className="dashboard-container">
      {/* HEADER */}
      <div className="dashboard-header">
        <div className="header-left">
          <Dropdown
            name="campaign_id"
            id="campaign_id"
            placeholder="Select Campaign"
            value={activeCompany}
            onChange={handleCampaignChange}
            options={campaignOptions}
            selectLabelEnabled
            selectLabel="Select Campaign"
          />
           <Dropdown
            name="duration"
            id="duration"
            placeholder="Select Duration"
            value={activeDuration}
            onChange={handleDurationChange}
            options={durationOptions}
            selectLabelEnabled
            selectLabel="Select Duration"
          />
        </div>

        <div className="header-right">
          <button
            className="refresh-btn"
            onClick={() => fetchDashboardData(activeCompany)}
            title="Refresh Dashboard"
          >
            <RefreshCcw
              size={20}
              className={isRefreshing ? "spin" : ""}
              style={{ color: "#2c3e66" }}
            />
          </button>
        </div>
      </div>
       {loading ? (
  <Loader fullScreen={false} message="Loading..." />
) : (<>

      {/* KPI STRIP */}
      {currentCompany && (
        <div className="kpi-strip">
          {currentCompany.kpis.map((kpi, i) => (
            <div className="kpi" key={i}>
              <p className="kpi-label">{kpi.label}</p>
              <div className="kpi-row">
                <span className="kpi-value">
                  <CountUp
                    end={Number(kpi.value)}
                    duration={3}
                    separator=","
                    preserveValue
                  />
                </span>
                <span
                  className={`kpi-delta ${
                    kpi.positive ? "pos" : "neg"
                  }`}
                >
                  {kpi.delta}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CAMPAIGN CARD */}
      {currentCompany && (
        <div className="campaign-card active">
          <div className="campaign-card__header">
            <div className="campaign-title">
              {currentCompany.campaign.name}
            </div>
          </div>

          <div className="campaign-grid">
            <div className="campaign-col">
              <div className="campaign-row">
                <span className="muted">Start Time:</span>
                <strong>
                  {currentCompany.campaign.startTime || "N/A"}
                </strong>
              </div>

              <div className="campaign-row">
                <span className="muted">Total Leads:</span>
                <strong>
                  <CountUp
                    end={currentCompany.campaign.totalPolicies}
                    duration={3}
                    preserveValue
                  />
                </strong>
              </div>
            </div>

            <div className="campaign-col">
              <div className="campaign-row">
                <span className="muted">End Time:</span>
                <strong>
                  {currentCompany.campaign.endTime || "N/A"}
                </strong>
              </div>
            </div>

            {/* HEALTH */}
            <div className="campaign-health">
              <div className="muted">Campaign Health</div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginTop: "8px",
                }}
              >
                <div className="health-score">
                  <span className="score">
                    <CountUp
                      end={currentCompany.campaign.healthScore}
                      duration={0.7}
                      preserveValue
                    />
                  </span>
                  <span className="score-total">/ 100</span>
                </div>

                <div style={{ width: 60, height: 60 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          {
                            value:
                              currentCompany.campaign.healthScore,
                            fill: "#4CAF50",
                          },
                          {
                            value:
                              100 -
                              currentCompany.campaign.healthScore,
                            fill: "#e0e0e0",
                          },
                        ]}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        innerRadius={18}
                        outerRadius={25}
                        cornerRadius={5}
                        isAnimationActive
                        animationDuration={600}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="health-trend">
                <span
                  className={`trend ${
                    currentCompany.campaign.healthDelta?.startsWith("+")
                      ? "pos"
                      : "neg"
                  }`}
                >
                  {currentCompany.campaign.healthDelta}
                </span>
                <span
                  className={`pill ${
                    currentCompany.campaign.healthStatus ===
                    "On Track"
                      ? "on-track"
                      : "needs-attention"
                  }`}
                >
                  {currentCompany.campaign.healthStatus}
                </span>
              </div>
            </div>
          </div>

          <div className="campaign-footer">
            <span className="chev" onClick={prevCampaign}>
              ‹
            </span>
            <span className="muted">
              {campaignOptions?.length > 1
                ? `${campaignOptions?.length - 1} more campaigns running...`
                : "No other campaigns"}
            </span>
            <span className="chev" onClick={nextCampaign}>
              ›
            </span>
          </div>
        </div>
      )}

      {/* PIE CHART */}
      {currentCompany && (
        <div className="chart-card">
          <h5>Disposition Breakdown</h5>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={currentCompany.disposition}
                dataKey="value"
                nameKey="label"
                outerRadius={80}
                label
                isAnimationActive
                animationDuration={600}
              >
                {currentCompany.disposition.map((d, i) => (
                  <Cell
                    key={i}
                    fill={
                      dispositionColors[d.label] || "#2196F3"
                    }
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      </>)}
    </div>
  );
}
